(() => {
  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-cadeau-builder]').forEach((root) => {
      if (root.dataset.cadeauReady === 'true') return;
      root.dataset.cadeauReady = 'true';

      const stages = [...root.querySelectorAll('[data-cadeau-stage]')];
      const stageNav = [...root.querySelectorAll('[data-cadeau-stage-nav]')];
      const sizeButtons = [...root.querySelectorAll('[data-cadeau-size]')];
      const categoryTabs = [...root.querySelectorAll('[data-cadeau-category-tab]')];
      const cards = [...root.querySelectorAll('[data-cadeau-item]')];
      const nextStageButton = root.querySelector('[data-cadeau-next-stage]');
      const previousStageButton = root.querySelector('[data-cadeau-previous-stage]');
      const categoryBack = root.querySelector('[data-cadeau-category-back]');
      const categoryNext = root.querySelector('[data-cadeau-category-next]');
      const categoryNextLabel = root.querySelector('[data-cadeau-category-next-label]');
      const categoryProgress = root.querySelector('[data-cadeau-category-progress]');
      const categoryEmpty = root.querySelector('[data-cadeau-category-empty]');
      const currentCategoryNumber = root.querySelector('[data-cadeau-current-number]');
      const currentCategoryTitle = root.querySelector('[data-cadeau-current-title]');
      const currentCategoryDescription = root.querySelector('[data-cadeau-current-description]');
      const selectionList = root.querySelector('[data-cadeau-selection]');
      const selectionEmpty = root.querySelector('[data-cadeau-empty]');
      const boxTitle = root.querySelector('[data-cadeau-box-title]');
      const boxPrice = root.querySelector('[data-cadeau-box-price]');
      const spaceUsed = root.querySelector('[data-cadeau-space-used]');
      const spaceTotal = root.querySelector('[data-cadeau-space-total]');
      const meter = root.querySelector('[data-cadeau-meter]');
      const total = root.querySelector('[data-cadeau-total]');
      const submit = root.querySelector('[data-cadeau-submit]');
      const submitLabel = submit?.querySelector('span');
      const status = root.querySelector('[data-cadeau-status]');
      const cartLink = root.querySelector('[data-cadeau-cart]');
      const recipient = root.querySelector('[data-cadeau-recipient]');
      const sender = root.querySelector('[data-cadeau-sender]');
      const message = root.querySelector('[data-cadeau-message]');
      const messageCount = root.querySelector('[data-cadeau-message-count]');
      const summary = root.querySelector('[data-cadeau-summary]');
      const minimumItems = Number(root.dataset.minimumItems || 1);

      const locale = document.documentElement.lang || 'it-IT';
      const currency = root.dataset.currency || 'EUR';
      const money = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });

      const categoryMeta = new Map(categoryTabs.map((tab) => [tab.dataset.cadeauCategoryTab, {
        tab,
        title: tab.querySelector('b')?.textContent.trim() || '',
        number: tab.querySelector('span')?.textContent.trim() || '',
        description: tab.dataset.description || ''
      }]));

      const items = cards.map((card, index) => ({
        key: String(index),
        card,
        variantId: card.dataset.variantId,
        title: card.dataset.title,
        price: Number(card.dataset.price || 0),
        category: card.dataset.category,
        categoryLabel: categoryMeta.get(card.dataset.category)?.title || '',
        sizes: card.dataset.sizes,
        space: Number(card.dataset.space || 1),
        available: card.dataset.available === 'true'
      }));

      const state = {
        size: '',
        stage: 0,
        furthestStage: 0,
        category: '',
        selected: new Map(),
        busy: false,
        added: false
      };

      const currentBox = () => sizeButtons.find((button) => button.dataset.cadeauSize === state.size);
      const quantityFor = (item) => state.selected.get(item.key) || 0;
      const selectedEntries = () => items.filter((item) => quantityFor(item) > 0);
      const selectedQuantity = () => selectedEntries().reduce((sum, item) => sum + quantityFor(item), 0);
      const usedCapacity = () => selectedEntries().reduce((sum, item) => sum + quantityFor(item) * item.space, 0);
      const bottleQuantity = () => selectedEntries().reduce((sum, item) => sum + (item.category === 'wine' ? quantityFor(item) : 0), 0);
      const eligibleForSize = (item) => {
        if (!state.size) return false;
        if (item.category === 'wine' && state.size !== 'large') return false;
        return item.sizes === 'both' || item.sizes === state.size;
      };
      const categoryQuantity = (category) => items.filter((item) => item.category === category).reduce((sum, item) => sum + quantityFor(item), 0);
      const eligibleCategories = () => categoryTabs.filter((tab) => items.some((item) => item.category === tab.dataset.cadeauCategoryTab && eligibleForSize(item)));

      const setStatus = (text = '', isError = false) => {
        if (!status) return;
        status.textContent = text;
        status.classList.toggle('is-error', isError);
      };

      const resetSuccess = () => {
        state.added = false;
        if (cartLink) cartLink.hidden = true;
      };

      const scrollToJourney = () => {
        if (matchMedia('(max-width: 900px)').matches) {
          root.querySelector('.cadeau-builder__journey')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      const showStage = (index, shouldScroll = true) => {
        const target = Math.max(0, Math.min(index, stages.length - 1));
        if (target > 0) {
          const box = currentBox();
          if (!box || box.dataset.available !== 'true') {
            setStatus(root.dataset.errorBox, true);
            return false;
          }
        }

        state.stage = target;
        state.furthestStage = Math.max(state.furthestStage, target);
        stages.forEach((stage, stageIndex) => {
          const active = stageIndex === target;
          stage.hidden = !active;
          stage.classList.toggle('is-active', active);
        });
        stageNav.forEach((button, stageIndex) => {
          const active = stageIndex === target;
          button.disabled = stageIndex > state.furthestStage;
          button.classList.toggle('is-active', active);
          button.classList.toggle('is-complete', stageIndex < target || stageIndex < state.furthestStage);
          if (active) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });
        if (target === 1) ensureCurrentCategory();
        setStatus();
        render();
        if (shouldScroll) scrollToJourney();
        return true;
      };

      const ensureCurrentCategory = () => {
        const eligible = eligibleCategories();
        if (!eligible.some((tab) => tab.dataset.cadeauCategoryTab === state.category)) {
          state.category = eligible[0]?.dataset.cadeauCategoryTab || '';
        }
      };

      const selectCategory = (category, shouldScroll = false) => {
        const eligible = eligibleCategories();
        if (!eligible.some((tab) => tab.dataset.cadeauCategoryTab === category)) return;
        state.category = category;
        renderCategory();
        renderCards();
        if (shouldScroll && matchMedia('(max-width: 720px)').matches) {
          root.querySelector('.cadeau-showroom')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      const selectSize = (size, announce = true) => {
        const button = sizeButtons.find((candidate) => candidate.dataset.cadeauSize === size);
        if (!button || button.dataset.available !== 'true') return;
        if (state.size === size) return;
        const hadSelection = state.selected.size > 0;
        state.size = size;
        state.selected.clear();
        ensureCurrentCategory();
        sizeButtons.forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (nextStageButton) nextStageButton.disabled = false;
        resetSuccess();
        if (announce && hadSelection) setStatus(root.dataset.sizeChanged);
        else setStatus();
        render();
      };

      const changeQuantity = (item, direction) => {
        if (state.busy || !item.available || !eligibleForSize(item)) return;
        const box = currentBox();
        if (!box) {
          setStatus(root.dataset.errorBox, true);
          return;
        }
        const current = quantityFor(item);
        if (direction > 0) {
          const capacity = Number(box.dataset.capacity || 0);
          const bottleLimit = Number(box.dataset.bottleLimit || 1);
          if (usedCapacity() + item.space > capacity) {
            setStatus(root.dataset.errorCapacity, true);
            return;
          }
          if (item.category === 'wine' && bottleQuantity() + 1 > bottleLimit) {
            setStatus(root.dataset.errorBottle, true);
            return;
          }
          state.selected.set(item.key, current + 1);
        } else if (current > 1) {
          state.selected.set(item.key, current - 1);
        } else {
          state.selected.delete(item.key);
        }
        setStatus();
        resetSuccess();
        render();
      };

      const renderProgress = () => {
        const boxReady = currentBox()?.dataset.available === 'true';
        if (nextStageButton) nextStageButton.disabled = !boxReady;
      };

      const renderCategory = () => {
        const eligible = eligibleCategories();
        ensureCurrentCategory();
        const currentIndex = eligible.findIndex((tab) => tab.dataset.cadeauCategoryTab === state.category);
        const meta = categoryMeta.get(state.category);

        categoryTabs.forEach((tab) => {
          const category = tab.dataset.cadeauCategoryTab;
          const hasProducts = eligible.includes(tab);
          const active = category === state.category;
          const count = categoryQuantity(category);
          tab.hidden = !hasProducts;
          tab.classList.toggle('is-active', active);
          tab.classList.toggle('has-selection', count > 0);
          tab.setAttribute('aria-pressed', active ? 'true' : 'false');
          const countNode = tab.querySelector('[data-cadeau-category-count]');
          if (countNode) countNode.textContent = count;
        });

        if (currentCategoryNumber) currentCategoryNumber.textContent = meta?.number || '—';
        if (currentCategoryTitle) currentCategoryTitle.textContent = meta?.title || '';
        if (currentCategoryDescription) currentCategoryDescription.textContent = meta?.description || '';
        if (categoryProgress) categoryProgress.textContent = eligible.length ? `${String(currentIndex + 1).padStart(2, '0')} / ${String(eligible.length).padStart(2, '0')}` : '—';
        if (categoryBack) categoryBack.textContent = currentIndex <= 0 ? `← ${root.dataset.backToSizeLabel}` : `← ${root.dataset.previousCategoryLabel}`;
        if (categoryNextLabel) categoryNextLabel.textContent = currentIndex === eligible.length - 1 ? root.dataset.toMessageLabel : root.dataset.nextCategoryLabel;
        if (categoryNext) categoryNext.disabled = eligible.length === 0;
      };

      const renderCards = () => {
        const box = currentBox();
        const capacity = Number(box?.dataset.capacity || 0);
        const bottleLimit = Number(box?.dataset.bottleLimit || 1);
        let visibleCount = 0;
        items.forEach((item) => {
          const visible = eligibleForSize(item) && item.category === state.category;
          item.card.hidden = !visible;
          if (visible) visibleCount += 1;
          const quantity = quantityFor(item);
          item.card.classList.toggle('is-selected', quantity > 0);
          const quantityLabel = item.card.querySelector('[data-cadeau-quantity]');
          const minus = item.card.querySelector('[data-cadeau-minus]');
          const plus = item.card.querySelector('[data-cadeau-add]');
          if (quantityLabel) quantityLabel.textContent = quantity;
          if (minus) minus.disabled = quantity === 0 || state.busy;
          if (plus) {
            const noSpace = usedCapacity() + item.space > capacity;
            const bottleFull = item.category === 'wine' && bottleQuantity() >= bottleLimit;
            plus.disabled = !item.available || state.busy || noSpace || bottleFull;
          }
        });
        if (categoryEmpty) categoryEmpty.hidden = visibleCount > 0 || eligibleCategories().length === 0;
      };

      const createSummaryItem = (item) => {
        const row = document.createElement('div');
        const copy = document.createElement('div');
        const title = document.createElement('b');
        const detail = document.createElement('small');
        const controls = document.createElement('div');
        const minus = document.createElement('button');
        const quantity = document.createElement('span');
        const plus = document.createElement('button');

        row.className = 'cadeau-summary__item';
        copy.className = 'cadeau-summary__item-copy';
        controls.className = 'cadeau-summary__item-controls';
        title.textContent = item.title;
        detail.textContent = money.format(item.price / 100);
        minus.type = 'button';
        minus.textContent = '−';
        minus.setAttribute('aria-label', `− ${item.title}`);
        plus.type = 'button';
        plus.textContent = '+';
        plus.setAttribute('aria-label', `+ ${item.title}`);
        quantity.textContent = quantityFor(item);
        minus.addEventListener('click', () => changeQuantity(item, -1));
        plus.addEventListener('click', () => changeQuantity(item, 1));

        copy.append(title, detail);
        controls.append(minus, quantity, plus);
        row.append(copy, controls);
        return row;
      };

      const renderSelection = () => {
        selectionList.replaceChildren();
        const entries = selectedEntries();
        selectionEmpty.hidden = entries.length > 0;
        categoryTabs.forEach((tab) => {
          const category = tab.dataset.cadeauCategoryTab;
          const categoryItems = entries.filter((item) => item.category === category);
          if (!categoryItems.length) return;
          const group = document.createElement('section');
          const heading = document.createElement('h4');
          const count = document.createElement('span');
          group.className = 'cadeau-summary__group';
          heading.textContent = categoryMeta.get(category)?.title || category;
          count.textContent = String(categoryQuantity(category)).padStart(2, '0');
          heading.append(count);
          group.append(heading);
          categoryItems.forEach((item) => group.append(createSummaryItem(item)));
          selectionList.append(group);
        });
      };

      const renderSummary = () => {
        const box = currentBox();
        const capacity = Number(box?.dataset.capacity || 0);
        const used = usedCapacity();
        const boxAmount = Number(box?.dataset.price || 0);
        const itemsAmount = selectedEntries().reduce((sum, item) => sum + item.price * quantityFor(item), 0);
        const boxReady = Boolean(box?.dataset.variantId) && box?.dataset.available === 'true';
        const enoughItems = selectedQuantity() >= minimumItems;

        boxTitle.textContent = box?.dataset.title || '—';
        boxPrice.textContent = boxReady ? money.format(boxAmount / 100) : '—';
        spaceUsed.textContent = used;
        spaceTotal.textContent = capacity;
        meter.style.transform = `scaleX(${Math.min(1, capacity ? used / capacity : 0)})`;
        total.textContent = boxReady ? money.format((boxAmount + itemsAmount) / 100) : '—';
        renderSelection();

        submit.disabled = state.busy || state.added || !boxReady || !enoughItems;
        if (submitLabel) {
          if (state.busy) submitLabel.textContent = root.dataset.addingLabel;
          else if (state.added) submitLabel.textContent = root.dataset.addedLabel;
          else if (!boxReady) submitLabel.textContent = root.dataset.chooseBoxLabel;
          else if (!enoughItems) submitLabel.textContent = root.dataset.chooseProductsLabel;
          else submitLabel.textContent = root.dataset.addLabel;
        }
      };

      function render() {
        renderProgress();
        renderCategory();
        renderCards();
        renderSummary();
      }

      sizeButtons.forEach((button) => button.addEventListener('click', () => selectSize(button.dataset.cadeauSize)));
      nextStageButton?.addEventListener('click', () => showStage(1));
      previousStageButton?.addEventListener('click', () => showStage(1));

      stageNav.forEach((button) => button.addEventListener('click', () => {
        const target = Number(button.dataset.cadeauStageNav);
        if (target <= state.furthestStage) showStage(target);
      }));

      categoryTabs.forEach((tab) => tab.addEventListener('click', () => selectCategory(tab.dataset.cadeauCategoryTab, true)));

      categoryBack?.addEventListener('click', () => {
        const eligible = eligibleCategories();
        const index = eligible.findIndex((tab) => tab.dataset.cadeauCategoryTab === state.category);
        if (index <= 0) showStage(0);
        else selectCategory(eligible[index - 1].dataset.cadeauCategoryTab, true);
      });

      categoryNext?.addEventListener('click', () => {
        const eligible = eligibleCategories();
        const index = eligible.findIndex((tab) => tab.dataset.cadeauCategoryTab === state.category);
        if (index < 0 || index === eligible.length - 1) showStage(2);
        else selectCategory(eligible[index + 1].dataset.cadeauCategoryTab, true);
      });

      items.forEach((item) => {
        item.card.querySelector('[data-cadeau-add]')?.addEventListener('click', () => changeQuantity(item, 1));
        item.card.querySelector('[data-cadeau-minus]')?.addEventListener('click', () => changeQuantity(item, -1));
      });

      message?.addEventListener('input', () => {
        if (messageCount) messageCount.textContent = message.value.length;
      });

      root.querySelector('[data-cadeau-review]')?.addEventListener('click', () => {
        summary?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      submit?.addEventListener('click', async () => {
        const box = currentBox();
        if (!box?.dataset.variantId || box.dataset.available !== 'true') {
          setStatus(root.dataset.errorBox, true);
          showStage(0);
          return;
        }
        if (selectedQuantity() < minimumItems) {
          setStatus(root.dataset.errorItems, true);
          showStage(1);
          return;
        }

        const compositionId = `ECLAT-${Date.now().toString(36).toUpperCase()}`;
        const properties = {
          'Formato cadeau': box.dataset.title,
          '_Cadeau ID': compositionId
        };
        if (recipient?.value.trim()) properties.Destinatario = recipient.value.trim();
        if (sender?.value.trim()) properties.Da = sender.value.trim();
        if (message?.value.trim()) properties.Messaggio = message.value.trim();

        const cartItems = [{ id: Number(box.dataset.variantId), quantity: 1, properties }];
        selectedEntries().forEach((item) => {
          cartItems.push({
            id: Number(item.variantId),
            quantity: quantityFor(item),
            properties: {
              '_Cadeau ID': compositionId,
              '_Parte di': box.dataset.title,
              '_Categoria cadeau': item.categoryLabel
            }
          });
        });

        state.busy = true;
        submit.setAttribute('aria-busy', 'true');
        setStatus();
        render();

        try {
          const shopRoot = window.Shopify?.routes?.root || '/';
          const base = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
          const response = await fetch(`${base}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: cartItems })
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || root.dataset.errorGeneric);

          const cart = await fetch(`${base}cart.js`).then((cartResponse) => cartResponse.json());
          document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
          state.added = true;
          if (submitLabel) submitLabel.textContent = root.dataset.addedLabel;
          if (cartLink) cartLink.hidden = false;
          setStatus(root.dataset.addedLabel);
        } catch (error) {
          setStatus(error.message || root.dataset.errorGeneric, true);
        } finally {
          state.busy = false;
          submit.removeAttribute('aria-busy');
          render();
        }
      });

      const firstAvailableSize = sizeButtons.find((button) => button.dataset.available === 'true');
      if (firstAvailableSize) selectSize(firstAvailableSize.dataset.cadeauSize, false);
      else render();
      showStage(0, false);
    });
  };

  if (!window.EclatCadeauBuilderInit) {
    window.EclatCadeauBuilderInit = initialise;
    document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  }
  initialise();
})();
